import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Versandschutz-schuifje — Bouwbrief 01 §6 voetpaneel a, gekoppeld 13 sep 2026.
 *
 * De stand komt uit de winkelwagen zelf: staat de regel erin, dan staat het
 * schuifje aan. Geen localStorage, geen cookie.
 *
 * STANDAARD AAN (alleen als data-default-on="true", thema-instelling
 * mivoza_addon_default_on). Besluit Sjoerd 14 sep 2026, als A/B-test, na
 * uitleg dat een vooraf aangevinkte betaalde extra in DE/AT niet mag
 * (§ 312a Abs. 3 BGB). Dan geldt:
 *
 *  - ligt er een echt product in de wagen en staat Versandschutz er niet in,
 *    en heeft de klant hem niet eerder zelf uitgezet → toevoegen;
 *  - zet de klant hem uit → winkelwagenattribuut _mv_versandschutz_uit = 1,
 *    zodat hij voor die wagen niet terugkomt;
 *  - zet de klant hem daarna weer aan → attribuut leeg.
 *
 * Los van standaard-aan: staat Versandschutz als enige regel in de wagen
 * (alle echte producten eruit gehaald), dan gaat hij eruit. Een
 * verzendbescherming zonder zending is een betaling voor niets.
 *
 * Het verversen loopt via CartLinesUpdateEvent, hetzelfde pad dat
 * product-form.js gebruikt. Daardoor werken lade, mandje-icoon en header
 * vanzelf mee.
 */

const ATTRIBUUT = '_mv_versandschutz_uit';

/*
 * De lade wordt na elke wijziging opnieuw getekend, en dan draait
 * connectedCallback opnieuw. Deze vlag voorkomt dat twee exemplaren tegelijk
 * dezelfde automatische actie starten.
 */
let automatischBezig = false;

class MivozaCartAddon extends HTMLElement {
  #bezig = false;

  connectedCallback() {
    this.#switch?.addEventListener('click', this.#onToggle);
    // Pas na het tekenen kijken, anders zit de lade nog midden in een morph.
    queueMicrotask(() => this.#controleerAutomatisch());
  }

  disconnectedCallback() {
    this.#switch?.removeEventListener('click', this.#onToggle);
  }

  get #switch() {
    return this.querySelector('.mv-addon__switch');
  }

  /** Alle secties die na een wijziging opnieuw getekend moeten worden. */
  get #secties() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean)
      .join(',');
  }

  get #wortel() {
    return window.Shopify?.routes?.root ?? '/';
  }

  #onToggle = () => {
    const button = this.#switch;
    if (!button) return;
    const aanZetten = button.getAttribute('aria-checked') !== 'true';
    this.#zet(aanZetten, { handmatig: true });
  };

  /**
   * Automatisch toevoegen of weghalen, op basis van de verse winkelwagen en
   * niet op basis van de data-attributen alleen: die kunnen een tel achterlopen
   * op wat er net is toegevoegd.
   */
  /** Na een wijziging in de winkelwagen aangeroepen door de luisteraar onderaan. */
  controleer() {
    return this.#controleerAutomatisch({ vers: true });
  }

  /**
   * @param {{vers?: boolean}} [opties] vers = niet afhaken op de data-attributen;
   *   na een toevoeging lopen die achter op de echte winkelwagen.
   */
  async #controleerAutomatisch({ vers = false } = {}) {
    if (automatischBezig || this.#bezig) return;
    const variantId = Number(this.dataset.variantId);
    if (!variantId) return;

    const standaardAan = this.dataset.defaultOn === 'true';

    if (!vers) {
      const inWagen = this.dataset.inCart === 'true';
      const echte = Number(this.dataset.realCount || 0);
      // Snel afhaken als er op basis van de pagina zelf al niets te doen is.
      const misschienToevoegen = standaardAan && !inWagen && echte > 0 && this.dataset.optedOut !== 'true';
      const misschienWeghalen = inWagen && echte === 0;
      if (!misschienToevoegen && !misschienWeghalen) return;
    }

    automatischBezig = true;
    try {
      const cart = await fetch(`${this.#wortel}cart.js`).then((r) => r.json());
      const regel = cart.items.find((i) => i.variant_id === variantId);
      const echteStuks = cart.items
        .filter((i) => i.variant_id !== variantId)
        .reduce((som, i) => som + i.quantity, 0);
      const afgemeld = cart.attributes?.[ATTRIBUUT] === '1';

      if (!regel && echteStuks > 0 && standaardAan && !afgemeld) {
        await this.#zet(true, { handmatig: false });
      } else if (regel && echteStuks === 0) {
        if (regel.key) this.dataset.lineKey = regel.key;
        await this.#zet(false, { handmatig: false });
      }
    } catch (error) {
      console.error('[mivoza-cart-addon] automatisch', error);
    } finally {
      automatischBezig = false;
    }
  }

  /**
   * @param {boolean} aan
   * @param {{handmatig: boolean}} opties
   */
  async #zet(aan, { handmatig }) {
    const variantId = this.dataset.variantId;
    if (!variantId || this.#bezig) return;

    // Meteen meebewegen, anders voelt het schuifje traag. Gaat het mis, dan
    // draait de catch hieronder het weer terug.
    this.#bezig = true;
    this.#teken(aan);

    const beloofd = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: aan ? 'add' : 'remove',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: aan ? 1 : 0 }],
        promise: beloofd.promise,
      })
    );

    try {
      const sections = this.#secties;
      let response;

      if (aan) {
        response = await this.#post('/cart/add.js', {
          items: [{ id: Number(variantId), quantity: 1 }],
          sections,
        });
      } else {
        // Op de regelsleutel wijzigen en niet op het variant-ID: de sleutel
        // wijst één regel aan, ook als dezelfde variant ooit twee keer in de
        // wagen zou staan.
        const sleutel = this.dataset.lineKey || variantId;
        response = await this.#post('/cart/change.js', { id: sleutel, quantity: 0, sections });
      }

      /*
       * Alleen een handmatige keuze van de klant wordt onthouden. Haalt het
       * script hem zelf weg omdat de wagen leeg is, dan is dat geen afmelding:
       * legt de klant daarna iets nieuws in de wagen, dan geldt de standaard
       * gewoon weer.
       */
      if (handmatig) {
        await this.#post('/cart/update.js', { attributes: { [ATTRIBUUT]: aan ? '' : '1' } });
      }

      const cart = await fetch(`${this.#wortel}cart.js`).then((r) => r.json());

      beloofd.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'mivoza-cart-addon',
          sourceId: 'versandschutz',
          itemCount: aan ? 1 : 0,
          sections: response?.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error('[mivoza-cart-addon]', error);
      this.#teken(!aan);
      beloofd.reject(error);
    } finally {
      this.#bezig = false;
    }
  }

  #teken(aan) {
    this.#switch?.setAttribute('aria-checked', String(aan));
    this.classList.toggle('is-on', aan);
  }

  async #post(pad, body) {
    const url = pad.replace('/cart/', `${this.#wortel}cart/`).replace('//', '/');
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const fout = await res.json().catch(() => null);
      throw new Error(fout?.description || fout?.message || `HTTP ${res.status}`);
    }
    return res.json();
  }
}

if (!customElements.get('mivoza-cart-addon')) {
  customElements.define('mivoza-cart-addon', MivozaCartAddon);

  /*
   * Luisteren naar elke wijziging in de winkelwagen, niet alleen bij het laden.
   * Horizon tekent de lade na een toevoeging opnieuw door het bestaande element
   * bij te werken — connectedCallback draait dan niet nog eens. Zonder dit kwam
   * Versandschutz er pas bij na een herlaad, terwijl een klant juist vanuit de
   * lade meteen naar de kassa gaat. Gemeten 14 sep: 3 artikelen toegevoegd,
   * geen Versandschutz tot de pagina herladen werd.
   *
   * Wijzigingen die dit script zelf doet worden overgeslagen, anders ontstaat
   * een lus.
   */
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.source === 'mivoza-cart-addon' || detail?.didError) return;
        // Even wachten zodat de lade klaar is met hertekenen. Een timeout en
        // geen requestAnimationFrame: dat laatste pauzeert in een tabblad dat
        // niet in beeld is, en dan zou een klant die net wegklikt Versandschutz
        // missen.
        setTimeout(() => {
          const el = document.querySelector('mivoza-cart-addon');
          if (el instanceof MivozaCartAddon) el.controleer();
        }, 150);
      })
      .catch(() => {});
  });
}
